import { useEffect, useRef, useState } from 'react';

import ArrowIcon from '../../../../assets/img/icon-circle-arrow-scroll.svg';
import CompanyLogo from '../../../../components/CompanyLogo/CompanyLogo';
import Preloader from '../../../../components/Preloader/Preloader';
import { ChartLine } from '../../../ChartLine';
import ClickableDiv from '../../../ClickableDiv/ClickableDiv';
import ClickableImage from '../../../ClickableImage/ClickableImage';
import DiscoverButton from '../../../DiscoverButton/DiscoverButton';
import useIsMobile from '../../../hooks/useIsMobile';
import {
  HOT_GAPPERS_FIRST_PART_SCROLL_POSITION,
  HOT_GAPPERS_NEXT_BTN_ROW_COUNT,
  HOT_GAPPERS_ROW_COUNT,
} from '../../HotGappers.constants';
import { GapperRow, HotGappersListProps } from '../../HotGappers.types';
import PriceAndChange from '../PriceAndChange/PriceAndChange';

import { getGapperCardCount, hasMoreGappers } from './helpers';

import styles from '../../HotGappers.module.scss';

const PRELOADER_HEIGHT = 225;
const MAX_RIGHT_SCROLL_POSITION = 1400;

const HotGappersList = ({
  rows,
  totalElements,
  isGapUpSelected,
  isReady,
  onSymbolClick,
  onViewAllClick,
}: HotGappersListProps) => {
  const { isMobile } = useIsMobile();
  const hasRows = rows.length > 0;
  const gapperCardCount = getGapperCardCount(rows.length, totalElements, HOT_GAPPERS_ROW_COUNT);

  const hasMore = hasMoreGappers(rows.length, totalElements, HOT_GAPPERS_ROW_COUNT);
  const hasNext = gapperCardCount > HOT_GAPPERS_NEXT_BTN_ROW_COUNT;
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    setScrollPosition(0);
  }, [isGapUpSelected]);

  const scrollLeft = () => {
    setScrollPosition(scrollPosition - HOT_GAPPERS_FIRST_PART_SCROLL_POSITION);
    scrollContainerRef.current?.scrollBy({ left: -HOT_GAPPERS_FIRST_PART_SCROLL_POSITION, behavior: 'smooth' });
  };

  const hasMoreRight = () => {
    if (gapperCardCount <= HOT_GAPPERS_NEXT_BTN_ROW_COUNT) {
      return false;
    }

    if (scrollPosition === 0) {
      return true;
    }

    if (gapperCardCount <= HOT_GAPPERS_ROW_COUNT) {
      return false;
    }

    return scrollPosition <= MAX_RIGHT_SCROLL_POSITION;
  };

  const scrollRight = () => {
    const scroll = scrollPosition + HOT_GAPPERS_FIRST_PART_SCROLL_POSITION;
    setScrollPosition(scroll);
    scrollContainerRef.current?.scrollBy({ left: scroll, behavior: 'smooth' });
  };

  if (!isReady && !hasRows) {
    return (
      <Preloader text='' style={{ height: PRELOADER_HEIGHT }} />
    );
  }

  const renderList = rows.slice(0, HOT_GAPPERS_ROW_COUNT).map((item: GapperRow) => (
    <ClickableDiv
      key={item.symbol}
      onClick={() => onSymbolClick(item.symbol)}
      className={styles.gapperCard}
    >
      <div className={styles.logoAndSymbol}>
        {(item.url || item.logo) && (
          <CompanyLogo
            className={styles.logo}
            url={item.url}
            logo={item.logo}
            symbol={item.symbol}
          />
        )}
        {' '}
        <p className={styles.symbol}>{item.symbol}</p>
        <p className={styles.displayName}>
          {item.displayName}
        </p>
      </div>
      <div className={styles.chart}>
        {isMobile && <div className={styles.chartCover} />}
        <ChartLine
          isChartInCard
          symbol={item.chartSymbol}
          isEuroStock={item.isEuroStock}
        />
      </div>
      <div className={styles.price}>
        <PriceAndChange item={item} />
      </div>
    </ClickableDiv>
  ));

  return (
    <div
      className={styles.listContainer}
    >
      {!isMobile && scrollPosition > (HOT_GAPPERS_FIRST_PART_SCROLL_POSITION - 1) && (
        <ClickableImage
          id="hotGappersPrevBtn"
          src={ArrowIcon}
          className={styles.buttonPrevious}
          onClick={scrollLeft}
        />
      )}
      <div
        className={styles.list}
        ref={scrollContainerRef}
      >
        {renderList}
        {hasMore && onViewAllClick && (
          <ClickableDiv
            onClick={onViewAllClick}
            className={styles.gapperCard}
          >
            <div className={styles.logoAndSymbol}>
              <DiscoverButton className={styles.viewAllBtn} onClick={() => {}} />
            </div>
          </ClickableDiv>
        )}
      </div>
      {!isMobile && hasNext && hasMoreRight() && (
        <ClickableImage
          id="hotGappersNextBtn"
          src={ArrowIcon}
          className={styles.buttonNext}
          onClick={scrollRight}
        />
      )}

    </div>
  );
};

export default HotGappersList;
